import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Header from "../../../components/Header";
import HomeButton from "../../../components/HomeButton";
import SpinnerLoader from "../../../components/SpinnerLoader";
import TabNavbar from "../../../components/Tabbar";
import {
  fetchWaitingPosition,
  getLearnerAppliedApplications,
  updateLearnerApplicationStatus,
  resetWaitingPosition,
} from "../../../redux/slices/learner.applications.slice";
import { RootState, useAppDispatch } from "../../../redux/store";
import { LEARNER_TABS } from "../../../utils/TabbarData";
import Pagination from "../../../components/Pagination";
import SearchWithFilter from "../../../components/SearchWithFilter";
import SaveConfirmModal from "../../../components/modal/SaveConfirmModal";
import RejectConfirmationModal from "../../../components/modal/RejectConfirmationModal";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { FaX } from "react-icons/fa6";
import { FaInfoCircle } from "react-icons/fa";
import EventDetailsModal from "../../Sessions/components/EventDetailsModal";

const STATUS_MAP: any = {
  APPLIED: {
    label: "Manager Approval Pending",
    color: "bg-orange-500 text-white",
    tooltip: "Your application is awaiting your manager's approval.",
  },
  APPROVED_BY_MANAGER: {
    label: "Waiting List",
    color: "bg-blue-500 text-white",
  },
  WAITING: {
    label: "Waiting List",
    color: "bg-blue-500 text-white",
    tooltip:
      "You are in the waiting list and will receive a spot once available.",
  },
  CONFIRMED_BY_LEARNER: {
    label: "Seat Confirmed",
    color: "bg-green-500 text-white",
    tooltip:
      "Seat secured. Enrolled. Cancel up to 7 days before the session starts.",
  },
  IN_PROGRESS: {
    label: "Session in Progress",
    color: "bg-purple-500 text-white",
  },
  REJECTED_BY_MANAGER: {
    label: "Denied by Manager",
    color: "bg-red-500 text-white",
    tooltip: "Your application was declined.",
  },
  REJECTED_BY_LEARNER: {
    label: "You Rejected",
    color: "bg-red-500 text-white",
    tooltip: "Your application was declined.",
  },
  APPLICATION_REJECTED_BY_LEARNER: {
    label: "You Rejected",
    color: "bg-red-500 text-white",
    tooltip: "Your application was declined.",
  },
  ENDED: { label: "Session Ended", color: "bg-gray-500 text-white" },
  REJECTED_BY_TOA: {
    label: "Denied by System",
    color: "bg-red-500 text-white",
    tooltip: "Your application was declined.",
  },
  REJECTED_BY_HR: {
    label: "HR DECLINED",
    color: "bg-red-500 text-white",
    tooltip: "Your application was declined.",
  },
  CONFIRMED_BY_HR: {
    label: "Confirmed by HR",
    color: "bg-green-500 text-white",
  },
  CONFIRMED_BY_MANAGER: {
    label: "Confirmed By Manager",
    color: "bg-green-500 text-white",
    tooltip: "Your manager has confirmed your application.",
  },
  DRAFTED: {
    label: "DRAFTED",
    color: "bg-gray-500 text-white",
  },
  HR_APPROVAL_PENDING: {
    label: "HR Approval Pending",
    color: "bg-orange-500 text-white",
    tooltip: "Your application is awaiting HR approval.",
  },
  APPROVED_BY_HR: {
    label: "Waiting List",
    color: "bg-blue-500 text-white",
  },
  ELIGIBLE: {
    label: "Eligible",
    color: "bg-green-500 text-white",
  },
  NOT_ELIGIBLE: {
    label: "Not Eligible",
    color: "bg-gray-500 text-white",
  },
};

function ApplicationStatus() {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1); // Current page state
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isCancleApplicationModalOpen, setIsCancleApplicationModalOpen] =
    useState(false);
  const [currentSessionName, setCurrentSessionName] = useState(null);
  const [currentStatus, setCurrentStatus] = useState<string>("");
  const [currentCourseId, setCurrentCourseId] = useState<number>(0);
  const [tooltipOpenId, setTooltipOpenId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal state
  const [modalData, setModalData] = useState<any[]>([]); // Modal data for events
  const [toCancelCourseName, setToCancelCourseName] = useState<string>("");
  const [toCancelSessionName, setToCancelSessionName] = useState<string>("");

  const [searchTerm, setSearchTerm] = useState("");
  const dispatch = useAppDispatch();

  const {
    appliedCourses,
    loading,
    updateLoading,
    waitingMessage,
    waitingMessageLoading,
    waitingError,
  } = useSelector((state: RootState) => state.learnerApplications);
  const [filteredCourses, setFilteredCourses] = useState<any[]>(appliedCourses);
  const totalPages =
    filteredCourses.length > 0
      ? Math.ceil(filteredCourses?.length / itemsPerPage)
      : 1;
  const paginatedData = filteredCourses.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getTranslatedWaitingMessage = (msg: string) => {
    const match = msg?.match(/position\s+(\d+)/i);
    if (match) {
      return t(
        "You are currently at position [NUMBER] in the waiting list for this course."
      ).replace("[NUMBER]", match[1]);
    }
    return t(msg);
  };

  useEffect(() => {
    const handleClickOutside = () => {
      if (tooltipOpenId !== null) {
        setTooltipOpenId(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [tooltipOpenId]);

  useEffect(() => {
    dispatch(getLearnerAppliedApplications());
  }, [dispatch]);

  useEffect(() => {
    setFilteredCourses(appliedCourses);
  }, [appliedCourses]);

  const handleCloseModal = () => setIsModalOpen(false);

  const handleApply = async () => {
    if (
      currentCourseId === 0 ||
      (currentStatus !== "CONFIRMED_BY_LEARNER" &&
        currentStatus !== "REJECTED_BY_LEARNER")
    )
      throw new Error("Please select a course and status");

    const payload = {
      userId: sessionStorage.getItem("userId") || "",
      courseId: currentCourseId,
      status: currentStatus,
    };

    await dispatch(updateLearnerApplicationStatus(payload));
    setIsSaveModalOpen(false);
    await dispatch(getLearnerAppliedApplications());
    setCurrentCourseId(0);
    setCurrentStatus("");
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
    const filteredData = appliedCourses.filter((course: any) => {
      return course.course_name.toLowerCase().includes(value.toLowerCase());
    });
    setFilteredCourses(filteredData);
  };

  const handleUpdateCourseApplication = async (
    course_id: number,
    status: string
  ) => {
    setCurrentStatus(status);
    setCurrentCourseId(course_id);
    setIsSaveModalOpen(true);
  };

  const handleRejectApplication = async () => {
    const payload = {
      userId: sessionStorage.getItem("userId") || "",
      courseId: currentCourseId,
      status: "APPLICATION_REJECTED_BY_LEARNER",
    };
    await dispatch(updateLearnerApplicationStatus(payload));
    setCurrentStatus("");
    setIsCancleApplicationModalOpen(false);
    setToCancelCourseName("");
    await dispatch(getLearnerAppliedApplications());
  };

  const handleRejectSession = async () => {
    const payload = {
      userId: sessionStorage.getItem("userId") || "",
      courseId: currentCourseId,
      status: "REJECTED_BY_LEARNER",
    };
    await dispatch(updateLearnerApplicationStatus(payload));
    setCurrentStatus("");
    setIsCancleApplicationModalOpen(false);
    setToCancelCourseName("");
    await dispatch(getLearnerAppliedApplications());
  };

  // Parse and set modal data for events
  const handleOpenModal = (events: string) => {
    // Step 1: Split by "," only outside of "({ })"
    // const eventBlocks = events.split(/(?<=\)}),/); // Match ", " only outside the "({ })"
    const eventBlocks = events.split(/;\s*/);
    const parsedEvents = eventBlocks.map((block) => {
      // Step 2: Split by "({"
      const [eventName, detailsBlock] = block.split("{(");

      // Step 3: Remove closing ")}" and split details inside "({ })" by ","
      const details = detailsBlock
        ?.replace(")}", "")
        ?.split(",")
        .map((item) => item.trim());

      // Step 4: Map details to fields
      const [date, time, mode, location, classroom, ...instructorsBlock] =
        details || [];

      // Step 5: Clean instructors
      // Step 5: Extract and clean instructors
      const instructors = instructorsBlock
        .join(", ") // Join remaining parts to recreate full instructors string
        .replace("Instructors:", "") // Remove the "Instructors:" prefix
        .trim();

      return {
        eventName: eventName?.trim(),
        date: date || null,
        time: time
          ? time
              .split("-")
              .map((t) => t.trim().slice(0, 5))
              .join("-")
          : null,
        mode: mode || null,
        location: location || null,
        classroom: classroom || null,
        instructors: instructors || null,
      };
    });

    setModalData(parsedEvents);
    setIsModalOpen(true);
  };

  const isMobileOrTablet = window.innerWidth < 1024; // Checking for mobile/tablet screens

  return (
    <div className="flex flex-col items-start gap-8">
      {!isMobileOrTablet && <HomeButton />}
      {isMobileOrTablet ? (
        <>
          {" "}
          <TabNavbar
            id=""
            tabs={LEARNER_TABS}
            selectedTabId={2}
            type="Application Status"
          />
          <HomeButton />
          <Header />
        </>
      ) : (
        <>
          <Header />
          <TabNavbar
            id=""
            tabs={LEARNER_TABS}
            selectedTabId={2}
            type="Application Status"
          />{" "}
        </>
      )}

      <div className="w-full h-full">
        <div className="w-full">
          <SearchWithFilter
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            disabled={loading}
          />
        </div>

        {/* Mobile/Tablet View */}
        <div className="block md:hidden w-full">
          {loading ? (
            <div className="flex items-center w-full justify-center mt-4 mb-4">
              <SpinnerLoader />
            </div>
          ) : paginatedData.length > 0 ? (
            paginatedData.map((item, i) => {
              const status = item?.applications?.[0]?.application_status;
              const sessionStartDate = moment(
                item?.applications?.[0]?.session_start_date
              );
              const daysDiff = sessionStartDate.diff(moment(), "days");
              const showCancelBtn =
                status === "CONFIRMED_BY_LEARNER" ||
                status === "APPLIED" ||
                status === "APPROVED_BY_MANAGER" ||
                status === "WAITING" ||
                status === "CONFIRMED_BY_MANAGER" ||
                status === "CONFIRMED_BY_HR" ||
                status === "HR_APPROVAL_PENDING" ||
                status === "APPROVED_BY_HR";
              const isDisabled = daysDiff < 7;
              return (
                <div
                  key={i}
                  className="border border-[#c6c6c6] py-2 text-left bg-[#f3f3f3] mt-2"
                >
                  <div className="px-4 border-b border-[#c6c6c6] pb-2">
                    <h3 className="font-[600] pb-2"> {t("Course Name")}</h3>
                    <p className="text-lg">{item?.course_name}</p>
                  </div>
                  <div className="px-4 border-b border-[#c6c6c6] pb-2">
                    <h3 className="font-[600] pb-2"> {t("Type")}</h3>
                    <p className="text-lg">{item?.category_type || "n/a"}</p>
                  </div>
                  <div className="px-4 border-b border-[#c6c6c6] pb-2">
                    <h3 className="font-[600] pb-2"> {t("Language")}</h3>
                    <p className="text-lg">
                      {item?.applications?.[0]?.drafted_session_id
                        ? item?.applications?.[0]?.session_language
                          ? item?.applications?.[0]?.session_language
                          : "NOT AVAILABLE"
                        : "TBD"}
                    </p>
                  </div>
                  <div className="px-4 border-b border-[#c6c6c6] pb-2">
                    <h3 className="font-[600] pb-2 ">{t("Session Details")}</h3>
                    <p className="text-lg">
                      {item?.applications?.[0]?.drafted_session_id ? (
                        <span
                          className="text-blue-500 cursor-pointer underline"
                          onClick={() => {
                            setCurrentSessionName(
                              item?.applications?.[0]?.session_name
                            );
                            handleOpenModal(item?.applications?.[0]?.events);
                          }}
                        >
                          {t("Open")}
                        </span>
                      ) : (
                        "TBD"
                      )}
                    </p>
                  </div>
                  <div className="px-4 pb-2">
                    <h3 className="font-[600] pb-2">{t("Status")}</h3>
                    {item?.applications?.[0]?.application_status !==
                    "DRAFTED" ? (
                      <div className="flex items-center py-2 px-4 text-center w-full justify-center">
                        <div className="relative group inline-block m-2">
                          <div
                            className={`min-w-[10rem] text-sm p-2 ${
                              STATUS_MAP[
                                item?.applications?.[0]?.application_status
                              ]?.color || "bg-red-500 text-white"
                            }`}
                          >
                            {t(
                              STATUS_MAP[
                                item?.applications?.[0]?.application_status
                              ]?.label || t("N/A")
                            )}
                          </div>

                          {/* Tooltip */}
                          {STATUS_MAP[
                            item?.applications?.[0]?.application_status
                          ]?.tooltip && (
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                              {t(
                                STATUS_MAP[
                                  item?.applications?.[0]
                                    ?.application_status
                                ]?.tooltip
                              )}
                            </div>
                          )}
                        </div>

                        {/* i button and tooltip */}
                        {item?.applications?.[0]?.application_status ===
                          "WAITING" && (
                          <div className="relative group">
                            <button
                              className="ml-2 text-blue-600 hover:text-blue-800"
                              onClick={(e) => {
                                e.stopPropagation(); // prevent global click
                                if (tooltipOpenId === item?.course_id) {
                                  setTooltipOpenId(null);
                                } else {
                                  setTooltipOpenId(item?.course_id);
                                  dispatch(resetWaitingPosition());
                                  dispatch(
                                    fetchWaitingPosition({
                                      userId:
                                        sessionStorage.getItem("userId") || "",
                                      courseId: item?.course_id,
                                    })
                                  );
                                }
                              }}
                            >
                              <FaInfoCircle size={16} />
                            </button>

                            {/* Tooltip */}
                            {tooltipOpenId === item?.course_id && (
                              <div
                                className={`absolute transition-opacity opacity-100 left-0 -translate-x-1/2 mt-2 w-max max-w-xs bg-white text-black border border-gray-300 text-sm rounded py-2 px-3 shadow-lg z-20 `}
                              >
                                {waitingMessageLoading ? (
                                  <div className="animate-pulse h-4 rounded w-3/4 mx-auto min-w-[100px]">
                                    <p>{t("Loading")}...</p>
                                  </div>
                                ) : waitingError ? (
                                  <p className="text-red-600">{waitingError}</p>
                                ) : (
                                  <p>{getTranslatedWaitingMessage(waitingMessage)}</p>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {showCancelBtn && (
                          <button
                            className={`ml-4 p-2 text-red-500 ${
                              isDisabled
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:text-red-700"
                            }`}
                            onClick={() => {
                              if (!isDisabled) {
                                setCurrentCourseId(item.course_id);
                                setIsCancleApplicationModalOpen(true);
                                setToCancelCourseName(item?.course_name);
                                setToCancelSessionName(item?.applications?.[0]?.session_name || "");
                                setCurrentStatus(
                                  item?.applications?.[0]?.application_status
                                );
                              }
                            }}
                            disabled={isDisabled}
                          >
                            <FaX size={20} />
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className={`m-2 min-w-[10rem] `}>
                        <div className="flex gap-2 align-center justify-center">
                          {/* Accept Button with Tooltip */}
                          <div className="relative group">
                            <button
                              className="p-2 bg-green-500 text-white"
                              onClick={() =>
                                handleUpdateCourseApplication(
                                  item?.course_id,
                                  "CONFIRMED_BY_LEARNER"
                                )
                              }
                            >
                              {t("Accept")}
                            </button>

                            {/* Tooltip */}
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                              {t("Confirm to secure your seat.")}
                            </div>
                          </div>
                          <button
                            className="p-2 bg-red-500 text-white"
                            onClick={() => {
                              setCurrentCourseId(item?.course_id);
                              setCurrentStatus("DRAFTED");
                              setToCancelCourseName(item?.course_name);
                              setToCancelSessionName(item?.applications?.[0]?.session_name || "");
                              setIsCancleApplicationModalOpen(true);
                            }}
                          >
                            {t("Reject")}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="w-full text-center py-5">{t("No applications available.")}</p>
          )}
        </div>

        {/* Desktop View */}
        <table className="hidden md:block table-auto w-full mt-4">
          <thead>
            <tr className="bg-[#A6192E] text-white">
              <th className="border-b border-[#961e38] py-2 px-4 w-1/5 text-center">
                {t("Course Name")}
              </th>
              <th className="border-b border-[#961e38] py-2 px-4 w-1/5 text-center">
                {t("Type")}
              </th>
              <th className="border-b border-[#961e38] py-2 px-4 w-1/5 text-center">
                {t("Language")}
              </th>
              <th className="border-b border-[#961e38] py-2 px-4 w-1/5 text-center">
                {t("Session Details")}
              </th>
              <th className="border-b border-[#961e38] py-2 px-4 w-1/5 text-center">
                {t("Status")}
              </th>
            </tr>
          </thead>
          <tbody className="align-middle">
            {loading ? (
              <tr>
                <td colSpan={8} className="text-center">
                  <div className="flex items-center w-full justify-center p-2 mt-4 mb-4">
                    <SpinnerLoader />
                  </div>
                </td>
              </tr>
            ) : (
              <>
                {paginatedData.length > 0 ? (
                  <>
                    {paginatedData?.map((item: any, i) => {
                      const status =
                        item?.applications?.[0]?.application_status;
                      const sessionStartDate = moment(
                        item?.applications?.[0]?.session_start_date
                      );
                      const daysDiff = sessionStartDate.diff(moment(), "days");
                      const showCancelBtn =
                        status === "CONFIRMED_BY_LEARNER" ||
                        status === "APPLIED" ||
                        status === "APPROVED_BY_MANAGER" ||
                        status === "WAITING" ||
                        status === "CONFIRMED_BY_MANAGER" ||
                        status === "CONFIRMED_BY_HR" ||
                        status === "HR_APPROVAL_PENDING" ||
                        status === "APPROVED_BY_HR";
                      const isDisabled = daysDiff < 7;
                      return (
                        <tr key={i}>
                          <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5">
                            {item?.course_name}
                          </td>
                          <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5">
                            {item?.category_type || "n/a"}
                          </td>
                          <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5">
                            {item?.applications?.[0]?.drafted_session_id
                              ? item?.applications?.[0]?.session_language
                                ? item?.applications?.[0]?.session_language
                                : "NOT AVAILABLE"
                              : "TBD"}
                          </td>
                          <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5">
                            {item?.applications?.[0]?.drafted_session_id ? (
                              <span
                                className="text-blue-500 cursor-pointer underline"
                                onClick={() => {
                                  setCurrentSessionName(
                                    item?.applications?.[0]?.session_name
                                  );
                                  handleOpenModal(
                                    item?.applications?.[0]?.events
                                  );
                                }}
                              >
                                {t("Open")}
                              </span>
                            ) : (
                              "TBD"
                            )}
                          </td>
                          {item?.applications?.[0]?.application_status !==
                          "DRAFTED" ? (
                            <td className="border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5 align-middle">
                              <div className="flex items-center justify-center gap-2">
                                <div className="relative group inline-block m-2">
                                  <div
                                    className={`min-w-[10rem] text-sm p-2 text-center rounded ${
                                      STATUS_MAP[
                                        item?.applications?.[0]
                                          ?.application_status
                                      ]?.color || "bg-red-500 text-white"
                                    }`}
                                  >
                                    {t(
                                      STATUS_MAP[
                                        item?.applications?.[0]
                                          ?.application_status
                                      ]?.label || t("N/A")
                                    )}
                                  </div>

                                  {/* Tooltip */}
                                  {STATUS_MAP[
                                    item?.applications?.[0]
                                      ?.application_status
                                  ]?.tooltip && (
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                                      {t(
                                        STATUS_MAP[
                                          item?.applications?.[0]
                                            ?.application_status
                                        ]?.tooltip
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* i button and tooltip */}
                                {item?.applications?.[0]?.application_status ===
                                  "WAITING" && (
                                  <div className="relative group">
                                    <button
                                      className="ml-2 text-blue-600 hover:text-blue-800"
                                      onClick={(e) => {
                                        e.stopPropagation(); // prevent global click
                                        if (tooltipOpenId === item?.course_id) {
                                          setTooltipOpenId(null);
                                        } else {
                                          setTooltipOpenId(item?.course_id);
                                          dispatch(resetWaitingPosition());
                                          dispatch(
                                            fetchWaitingPosition({
                                              userId:
                                                sessionStorage.getItem(
                                                  "userId"
                                                ) || "",
                                              courseId: item?.course_id,
                                            })
                                          );
                                        }
                                      }}
                                    >
                                      <FaInfoCircle size={16} />
                                    </button>

                                    {/* Tooltip */}
                                    {tooltipOpenId === item?.course_id && (
                                      <div className="absolute transition-opacity opacity-100 left-1/2 -translate-x-1/2 mt-2 w-max max-w-xs bg-white text-black border border-gray-300 text-sm rounded py-2 px-3 shadow-lg z-20">
                                        {waitingMessageLoading ? (
                                          <div className="animate-pulse h-4 rounded w-3/4 mx-auto min-w-[100px]">
                                            <p>{t("Loading")}...</p>
                                          </div>
                                        ) : waitingError ? (
                                          <p className="text-red-600">
                                            {waitingError}
                                          </p>
                                        ) : (
                                          <p>{getTranslatedWaitingMessage(waitingMessage)}</p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {showCancelBtn && (
                                  <button
                                    className={`ml-4 p-2 text-red-500 ${
                                      isDisabled
                                        ? "opacity-50 cursor-not-allowed"
                                        : "hover:text-red-700"
                                    }`}
                                    onClick={() => {
                                      if (!isDisabled) {
                                        setCurrentCourseId(item.course_id);
                                        setIsCancleApplicationModalOpen(true);
                                        setToCancelCourseName(
                                          item?.course_name
                                        );
                                        setToCancelSessionName(item?.applications?.[0]?.session_name || "");
                                        setCurrentStatus(
                                          item?.applications?.[0]
                                            ?.application_status
                                        );
                                      }
                                    }}
                                    disabled={isDisabled}
                                  >
                                    <FaX size={20} />
                                  </button>
                                )}
                              </div>
                            </td>
                          ) : (
                            <td
                              className={`border-b border-[#c6c6c6] py-2 px-4 text-center w-1/5`}
                            >
                              <div className={`m-2 min-w-[10rem] `}>
                                <div className="flex gap-2 align-center justify-center">
                                  {/* Accept Button with Tooltip */}
                                  <div className="relative group">
                                    <button
                                      className="p-2 bg-green-500 text-white"
                                      onClick={() =>
                                        handleUpdateCourseApplication(
                                          item?.course_id,
                                          "CONFIRMED_BY_LEARNER"
                                        )
                                      }
                                    >
                                      {t("Accept")}
                                    </button>

                                    {/* Tooltip */}
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                      {t("Confirm your participation")}
                                    </div>
                                  </div>
                                  <button
                                    className="p-2 bg-red-500 text-white"
                                    onClick={() => {
                                      setCurrentCourseId(item?.course_id);
                                      setCurrentStatus("DRAFTED");
                                      setToCancelCourseName(item?.course_name);
                                      setToCancelSessionName(item?.applications?.[0]?.session_name || "");
                                      setIsCancleApplicationModalOpen(true);
                                    }}
                                  >
                                    {t("Reject")}
                                  </button>
                                </div>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </>
                ) : (
                  <tr className="pt-5">
                    <td colSpan={3} className="text-center">
                      {t("No applications available.")}
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
      {loading === false && (
        <Pagination
          isCoursePage={true}
          totalPages={totalPages}
          _currentPage={currentPage}
          _itemsPerPage={itemsPerPage}
          total_item_count={filteredCourses.length}
          handleChange={({ page, itemsPerPage }) => {
            setCurrentPage(page);
            setItemsPerPage(itemsPerPage);
          }}
        />
      )}
      {isSaveModalOpen && (
        <SaveConfirmModal
          closeModal={() => setIsSaveModalOpen(false)}
          handleSave={handleApply}
          loading={updateLoading}
          msg="Are you sure you want to proceed with this action?"
        />
      )}
      {isCancleApplicationModalOpen && (
        <RejectConfirmationModal
          closeModal={() => {
            setIsCancleApplicationModalOpen(false);
            setToCancelCourseName("");
            setToCancelSessionName("");
          }}
          handleRejectApplication={handleRejectApplication}
          handleRejectSession={handleRejectSession}
          loading={updateLoading}
          msg={t("Please choose the action you wish to perform for [COURSE NAME] [SESSION NAME]:\n\nIf you were offered a seat, you can reject it and be placed in the Waiting List to wait for another seat to be offered to you by clicking on REJECT SEAT.\n\nIf you would like to delete your application to this course completely, please click DELETE APPLICATION.")
            .replace("[COURSE NAME]", `<strong>${toCancelCourseName}</strong>`)
            .replace("[SESSION NAME]", `<strong>${toCancelSessionName}</strong>`)}
          applicationStatus={currentStatus}
        />
      )}
      <EventDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        modalData={modalData}
        modalName="Session Details"
        sessionName={currentSessionName}
      />
    </div>
  );
}

export default ApplicationStatus;
